/**
 * Task Registry — auto-assembled from individual task definition files.
 *
 * ADDING A NEW TASK:
 * 1. Add task name to `onboardingTasks` in constants.ts
 * 2. Add data interface (if needed) in types.ts and TaskDataMap entry in task-data-map.ts
 * 3. Create a new file in tasks/ exporting a TaskDefinition, and add it to tasks/index.ts
 * 4. Add trigger logic in src/client/identify-policy-tasks.ts
 */

import type { OnboardingTaskName } from './constants';
import type { TaskDataMap } from './task-data-map';
import type { TaskConfig, ContentContext, RenderedTaskConfig, TaskInput } from './types';
import { allTaskDefinitions } from './tasks';

/**
 * Resolve a task's staff display name. Handles both the static string form and the
 * data-driven function form of `TaskConfig.staffDisplayName`.
 *
 * Falls back to `taskName` if no registry entry exists.
 */
export function resolveStaffDisplayName(taskName: string, data?: Record<string, any>): string {
  const config = taskRegistry[taskName as OnboardingTaskName];
  if (!config) {
    return taskName;
  }
  if (typeof config.staffDisplayName === 'function') {
    return config.staffDisplayName({ data: data || {} });
  }
  return config.staffDisplayName;
}

// Auto-build registry from task definitions.
// Note: `as` assertion is used because Object.fromEntries returns Record<string, V>.
// Exhaustiveness is enforced by TaskDefinition.taskName being typed as OnboardingTaskName —
// if a task name exists in onboardingTasks but has no definition file, it won't be in the registry at runtime.
// Consider adding a runtime assertion or test if strict coverage is needed.
export const taskRegistry = Object.fromEntries(allTaskDefinitions.map((def) => [def.taskName, def.config])) as Record<
  OnboardingTaskName,
  TaskConfig
>;

// ========== RENDERED CONFIG HELPER ==========

export function formatPolicies(policies: string[]): string {
  if (policies.length === 0) {
    return '';
  }
  if (policies.length === 1) {
    return policies[0];
  }
  if (policies.length === 2) {
    return `${policies[0]} and ${policies[1]}`;
  }
  return `${policies.slice(0, -1).join(', ')}, and ${policies[policies.length - 1]}`;
}

/** Resolve daysAllowed from config — handles both static number and state-dependent function. */
export function resolveDaysAllowed(daysAllowed: number | ((state: string) => number), state?: string): number {
  if (typeof daysAllowed === 'function') {
    return daysAllowed(state || '');
  }
  return daysAllowed;
}

/**
 * Resolve the registry's `autoApprove` field for a given submission. Handles all
 * three forms (`undefined` / `boolean` / predicate function) so callers don't have
 * to. Returns `false` for unknown task names.
 *
 * Generic on `taskName` so `taskData` is narrowed to the task's `TaskDataMap[T]`
 * shape at the call site (e.g. UDD auto-approve gets a typed `removedReason`).
 */
export function resolveAutoApprove<T extends keyof TaskDataMap>(
  taskName: T,
  taskData?: TaskDataMap[T] | null
): boolean {
  const config = taskRegistry[taskName as OnboardingTaskName];
  if (!config?.autoApprove) {
    return false;
  }
  if (typeof config.autoApprove === 'function') {
    try {
      return config.autoApprove((taskData ?? undefined) as Record<string, unknown> | undefined);
    } catch {
      return false;
    }
  }
  return config.autoApprove;
}

/**
 * Calls account.content() with a built context and returns rendered content.
 * Used by the Task component in account-fe.
 *
 * @param task - Task record with taskName, data, affectedPolicies, and deadline
 * @param brandContext - Optional brand strings. Defaults to Branch if omitted.
 */
export function getTaskConfig(
  task: TaskInput,
  brandContext?: { brandName?: string; brandFullName?: string }
): RenderedTaskConfig | null {
  const config = taskRegistry[task.taskName as OnboardingTaskName];
  if (!config?.account?.content) {
    return null;
  }

  const policyIds = task.affectedPolicies || [];

  const ctx: ContentContext = {
    affectedPolicies: formatPolicies(policyIds),
    daysAllowed: resolveDaysAllowed(config.daysAllowed),
    formattedDeadline: task.deadline
      ? new Date(task.deadline).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : '',
    data: (task.data || {}) as Record<string, any>,
    brandName: brandContext?.brandName || 'Branch',
    brandFullName: brandContext?.brandFullName || 'Branch Insurance'
  };

  return {
    ...config.account.content(ctx),
    toastMessage: config.account.toastMessage,
    affectedPoliciesLeadText: config.account.affectedPoliciesLeadText
  };
}
